import {WebflowElementText} from '@intellimize/dash-shared';
import {Component, Expressions} from '@packages/systems/wfdl';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  expandUserComponents,
  getChildren,
  getPageComponentRender,
  getPageComponentRootElement,
  getTag,
} from '@packages/utilities/platform';
import {
  isHeading,
  isListItem,
  isParagraph,
} from '@packages/utilities/elementTypeUtils';
import {getClosestOptimizationTreatment} from './getClosestOptimizationTreatment';

function mapDescendants<T>(
  element: ExpressionElement,
  fn: (el: ExpressionElement) => T | null,
  r?: (T | null)[]
): (T | null)[] | null {
  const results: (T | null)[] = r || [];
  const children = getChildren(element);
  if (!children) {
    return null;
  }
  children.forEach((child) => {
    const result = fn(child);
    if (result) {
      results.push(result);
    }
    return mapDescendants(child, fn, results);
  });
  return results;
}

export function getElementText(element: ExpressionElement): string | null {
  const childrenText = mapDescendants(element, (el) => {
    return Expressions.getText(Expressions.getElementData(el));
  });
  if (!childrenText) {
    return null;
  }
  return childrenText.filter(Boolean).join();
}

function getWebflowElementTextType(
  element: ExpressionElement
): WebflowElementText['type'] | null {
  if (isHeading(element)) {
    const tag = getTag(element);
    if (tag === 'h1') {
      return 'headline';
    }
    return 'subheadline';
  }
  if (isParagraph(element)) {
    return 'paragraph';
  }
  if (isListItem(element)) {
    return 'list-item';
  }
  return null;
}

export function getPageText(components: Component.ComponentMap) {
  const pageRootElement = getPageComponentRootElement(components);
  const pageComponentRender = getPageComponentRender(components);
  if (!pageRootElement || !pageComponentRender) {
    return null;
  }
  // Expand all component instances with their overrides applied
  const expandedRoot = Expressions.getElement(
    expandUserComponents(components)(pageRootElement)
  );
  if (!expandedRoot) {
    return null;
  }

  const result = mapDescendants(expandedRoot, (el) => {
    const webflowElementTextType = getWebflowElementTextType(el);
    if (!webflowElementTextType) {
      return null;
    }
    const isWrappedInVariation = getClosestOptimizationTreatment(
      el.id,
      pageComponentRender
    );
    if (isWrappedInVariation) {
      return null;
    }
    const text = getElementText(el);
    if (!text) {
      return null;
    }
    return {
      type: webflowElementTextType,
      text,
    };
  });
  const filtered =
    result?.filter((text): text is WebflowElementText => Boolean(text)) || [];
  return filtered;
}
